import { dropLocalSession, loadSession } from './session';

/**
 * The one way this app talks to the Availability API.
 *
 * Every page module used to carry its own copy of this — the base URL, an
 * error class, and a `post` that differed only in which error it threw. Three
 * copies meant three places to fix anything about the transport, which is how
 * the expired-session handling below went missing from all of them.
 *
 * What varies between pages is the request ENVELOPE — Availability's own
 * shape, the shared page envelope, the overlays' vocabulary — and that still
 * lives in `api.ts`, `pages.api.ts` and `overlayApi.ts`. Only the wire is
 * shared.
 */

export const BASE = (import.meta.env.VITE_API_BASE ?? 'http://localhost:8090').replace(/\/$/, '');

/**
 * Fired when the API refuses the session outright, so the app can return the
 * user to the sign-in screen.
 *
 * An event rather than a callback because the refusal surfaces inside whichever
 * hook happened to be fetching, and none of them own the auth state. The alt-
 * ernative — every hook checking for a 401 and calling up — is the arrangement
 * that let this be forgotten in the first place.
 */
export const SESSION_EXPIRED_EVENT = 'qcomm:session-expired';

/**
 * A failed request.
 *
 * `message` is the API's own sentence and nothing else, because the pages
 * render it straight to the user — `DataSourceBanner` and the overlay states
 * all show `error.message`. Two of the three old transports prefixed it with
 * the status and the route, which put "500 /v1/sales/overview:" in front of a
 * message written to be read. The status and the path are still here as
 * fields, which is where a console or a log wants them anyway.
 */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly path: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The bearer, and nothing else — there is no static fallback token to ship. */
function bearer(): Record<string, string> {
  const token = loadSession()?.token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

/** Pulls the API's `{ error: { message } }` out of a failed response body. */
function messageFrom(detail: unknown, fallback: string): string {
  return typeof detail === 'object' && detail !== null && 'error' in detail
    ? String((detail as { error?: { message?: string } }).error?.message ?? fallback)
    : fallback;
}

/**
 * A session the API no longer accepts.
 *
 * The local copy has to go: the token is syntactically fine, so the auth gate
 * keeps letting the dashboard render and every panel underneath it fails. That
 * left the user on a permanently broken page that a reload did not fix, because
 * the reload read the same dead token back out of sessionStorage.
 *
 * Note this is 401 only. The tenant guard refuses an un-granted brand with 403,
 * which is a scoping mistake and not a reason to sign anyone out.
 */
function sessionExpired(): void {
  dropLocalSession();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
  }
}

/**
 * One request path for every verb.
 *
 * The expiry handling above is the reason this is not three near-copies: a
 * verb that forgot to call `sessionExpired()` would leave exactly the
 * permanently-broken dashboard that handler exists to prevent.
 */
async function request<T>(
  method: 'GET' | 'POST' | 'PUT',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: { ...(body === undefined ? {} : { 'content-type': 'application/json' }), ...bearer() },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    signal: signal ?? null,
  });

  if (!res.ok) {
    let detail: unknown;
    try {
      detail = await res.json();
    } catch {
      detail = await res.text().catch(() => '');
    }
    if (res.status === 401) sessionExpired();
    throw new ApiError(messageFrom(detail, res.statusText), res.status, path, detail);
  }

  return (await res.json()) as T;
}

export async function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>('POST', path, body, signal);
}

export async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  return request<T>('GET', path, undefined, signal);
}

export async function put<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return request<T>('PUT', path, body, signal);
}
